import {
  getDeliveryRouteLocationLabel as getOrderLocationLabel,
  getDeliveryRouteLocationMismatchMessage,
  getDeliveryRouteSelectionId,
} from './delivery-share.utils'
import {
  MARKER_ACTIVE_ROUTE_COLOR,
  MARKER_AVAILABLE_COLOR,
  MARKER_DELIVERED_COLOR,
  MARKER_MISSED_COLOR,
  MARKER_MISSED_GLYPH_COLOR,
  MARKER_SELECTED_COLOR,
  getDeliveryRouteMapAddress,
} from './delivery-route-map-model'
import { isOrderBlockedByRouteLocation } from './delivery-route-map-routing'

export function getDeliveryRouteMapMarkerLabel(group) {
  return group?.orders?.length > 1 ? String(group.orders.length) : ''
}

export function getDeliveryRouteMapMarkerHandledState(group) {
  const orders = group?.orders ?? []

  if (orders.length === 0) {
    return null
  }

  const deliveredCount = orders.filter((order) => order?.is_delivered).length
  const missedCount = orders.filter((order) => order?.is_route_missed).length
  const handledCount = deliveredCount + missedCount

  if (handledCount === 0) {
    return null
  }

  if (handledCount < orders.length) {
    return 'mixed'
  }

  if (deliveredCount === orders.length) {
    return 'delivered'
  }

  if (missedCount === orders.length) {
    return 'missed'
  }

  return 'handled'
}

export function getDeliveryRouteMapMarkerStatusLabel(handledState) {
  if (handledState === 'delivered') {
    return 'Delivered'
  }

  if (handledState === 'missed') {
    return 'Missed'
  }

  if (handledState === 'handled') {
    return 'Handled'
  }

  if (handledState === 'mixed') {
    return 'Partially handled'
  }

  return ''
}

export function getDeliveryRouteMapMarkerDisplayLabel({ fallbackLabel, handledState }) {
  if (handledState === 'delivered' || handledState === 'handled') {
    return '✓'
  }

  if (handledState === 'missed') {
    return '!'
  }

  return fallbackLabel
}

export function getDeliveryRouteMapMarkerSelectionState(group, selectedOrderIds = []) {
  const selectedOrderIdSet =
    selectedOrderIds instanceof Set ? selectedOrderIds : new Set(selectedOrderIds)
  const selectedCount =
    group?.orders?.filter((order) => selectedOrderIdSet.has(getDeliveryRouteSelectionId(order)))
      .length ?? 0
  const orderCount = group?.orders?.length ?? 0

  return {
    isPartiallySelected: orderCount > 1 && selectedCount > 0 && selectedCount < orderCount,
    isSelected: orderCount > 0 && selectedCount === orderCount,
  }
}

export function isDeliveryRouteMapMarkerHighlighted(group, highlightedOrderIds = []) {
  const highlightedOrderIdSet =
    highlightedOrderIds instanceof Set ? highlightedOrderIds : new Set(highlightedOrderIds)

  return Boolean(
    group?.orders?.some((order) => highlightedOrderIdSet.has(getDeliveryRouteSelectionId(order)))
  )
}

export function getDeliveryRouteMapMarkerTone({
  handledState,
  isBlocked,
  isPartiallySelected = false,
  isReadOnlyRoute = false,
  isSelected,
}) {
  if (isBlocked) return { background: '#94a3b8', border: '#ffffff', glyph: '#ffffff' }
  if (handledState === 'delivered') {
    return { background: MARKER_DELIVERED_COLOR, border: '#ffffff', glyph: '#ffffff' }
  }
  if (handledState === 'missed') {
    return {
      background: MARKER_MISSED_COLOR,
      border: '#ffffff',
      glyph: MARKER_MISSED_GLYPH_COLOR,
    }
  }
  if (handledState === 'handled') {
    return {
      background: `linear-gradient(90deg, ${MARKER_DELIVERED_COLOR} 0 50%, ${MARKER_MISSED_COLOR} 50% 100%)`,
      border: '#ffffff',
      glyph: '#ffffff',
    }
  }
  if (handledState === 'mixed') {
    return {
      background: `linear-gradient(90deg, ${MARKER_DELIVERED_COLOR} 0 50%, ${MARKER_AVAILABLE_COLOR} 50% 100%)`,
      border: '#ffffff',
      glyph: '#ffffff',
    }
  }
  if (isReadOnlyRoute && isSelected) {
    return { background: MARKER_ACTIVE_ROUTE_COLOR, border: '#ffffff', glyph: '#ffffff' }
  }
  if (isPartiallySelected) {
    return {
      background: `linear-gradient(90deg, ${MARKER_SELECTED_COLOR} 0 50%, ${MARKER_AVAILABLE_COLOR} 50% 100%)`,
      border: '#ffffff',
      glyph: '#ffffff',
    }
  }
  if (isSelected) {
    return { background: MARKER_SELECTED_COLOR, border: '#ffffff', glyph: '#ffffff' }
  }

  return { background: MARKER_AVAILABLE_COLOR, border: '#ffffff', glyph: '#ffffff' }
}
export function getDeliveryRouteMapMarkerBlockedDescription(
  group,
  anchorOrder,
  selectedOrderIdSet
) {
  const blockedOrder = group?.orders?.find((order) =>
    isOrderBlockedByRouteLocation(order, anchorOrder, selectedOrderIdSet)
  )

  return getDeliveryRouteLocationMismatchMessage(blockedOrder, anchorOrder)
}

export function getDeliveryRouteMapMarkerTitle({
  anchorOrder,
  group,
  handledState,
  isBlocked,
  selectedOrderIdSet,
}) {
  const firstOrder = group.orders[0]
  const statusLabel = getDeliveryRouteMapMarkerStatusLabel(handledState)
  const titleBase =
    group.orders.length > 1
      ? `${group.orders.length} orders at ${getDeliveryRouteMapAddress(firstOrder)}`
      : `${firstOrder.recipient_name || `Order #${firstOrder.sales_order_id ?? firstOrder.id}`} - ${getOrderLocationLabel(firstOrder.location)}`
  const blockedDescription = isBlocked
    ? getDeliveryRouteMapMarkerBlockedDescription(group, anchorOrder, selectedOrderIdSet)
    : null
  const titlePrefix = blockedDescription ? `Unavailable: ${blockedDescription}` : statusLabel

  return titlePrefix ? `${titlePrefix} - ${titleBase}` : titleBase
}

export function getDeliveryRouteMapMarkerToggleActions({
  anchorOrder,
  group,
  selectedOrderIds = [],
}) {
  if (!group?.orders?.length) {
    return []
  }

  const selectedOrderIdSet = new Set(selectedOrderIds)
  const addActions = group.orders
    .filter(
      (order) =>
        !selectedOrderIdSet.has(getDeliveryRouteSelectionId(order)) &&
        !isOrderBlockedByRouteLocation(order, anchorOrder, selectedOrderIdSet)
    )
    .map((order) => ({ checked: true, orderId: getDeliveryRouteSelectionId(order) }))

  if (addActions.length > 0) {
    return addActions
  }

  return group.orders
    .filter((order) => selectedOrderIdSet.has(getDeliveryRouteSelectionId(order)))
    .map((order) => ({ checked: false, orderId: getDeliveryRouteSelectionId(order) }))
}
